import { PROD } from './env';

import type {
  Card,
  Category,
  CouponAvailability,
  CouponBenefits,
  CouponUsage,
  NotificationCategory,
  StampType,
  StampValue,
} from '@rewardopl/types';

export const APP_ID = 'com.rewardoapp.app';

export const BUSINESS_CATEGORIES = {
  GASTRONOMY: 'gastronomy',
  BEAUTY_AND_WELLNESS: 'beauty_and_wellness',
  SERVICES: 'services',
  ENTERTAINMENT: 'entertainment',
  HOSPITALITY_AND_TOURISM: 'hospitality_and_tourism',
  HEALTH: 'health',
  TRANSPORTATION: 'transportation',
  FASHION: 'fashion',
  OTHER: 'other',
  ONLINE: 'online',
} as const satisfies Record<string, Category>;

export const CARD_TYPES = {
  POINTS: 'points',
  STAMPS_STACKED: 'stamps_stacked',
} as const satisfies Record<string, Card['type']>;

export const COUNTRIES = {
  GERMANY: 'GERMANY',
  POLAND: 'POLAND',
  UK: 'UK',
} as const;

export const COUNTRY_CALLING_CODES = {
  GERMANY: '+49',
  POLAND: '+48',
  UK: '+44',
} as const satisfies Record<keyof typeof COUNTRIES, string>;

export const COUNTRY_CODES = {
  GERMANY: 'DE',
  POLAND: 'PL',
  UK: 'UK',
} as const satisfies Record<keyof typeof COUNTRIES, string>;

export const COUNTRY_NAMES = {
  GERMANY: 'Germany',
  POLAND: 'Poland',
  UK: 'United Kingdom',
} as const satisfies Record<keyof typeof COUNTRIES, string>;

export const COUPON_AVAILABILITY_TYPES = {
  DATETIME_RANGE: 'datetime_range',
  HOURS: 'hours',
  UNLIMITED: 'unlimited',
} as const satisfies Record<string, CouponAvailability['type']>;

export const COUPON_BENEFIT_TYPES = {
  DISCOUNT_PERCENTAGE: 'discount_percentage',
  DISCOUNT_AMOUNT: 'discount_amount',
  STAMP_AMOUNT: 'stamp_amount',
  STAMP_MULTIPLY: 'stamp_multiply',
  FREEBIE: 'freebie',
} as const satisfies Record<string, CouponBenefits['type']>;

export const COUPON_USAGE_TYPES = {
  ONCE: 'once',
  ONCE_EVERY: 'once_every',
  UNLIMITED: 'unlimited',
} as const satisfies Record<string, CouponUsage['type']>;

const isProduction = PROD;

export const GOOGLE_MAPS_API_KEY = isProduction
  ? 'AIzaSyD4Zbjw4ZtW25fD1dva_4_MZYmKo51nKlw'
  : 'AIzaSyADtUz4xTPae1kc8BZem8e0jUBgAHmMFZw';

export const MOBILE_PHONE_PREFIXES = {
  GERMANY: ['15', '16', '17'],
  POLAND: ['45', '50', '51', '53', '57', '60', '66', '69', '72', '73', '78', '79', '88'],
  UK: ['71', '73', '74', '75', '76', '77', '78', '79'],
} as const satisfies Record<keyof typeof COUNTRIES, unknown>;

export const MONTHS = {
  JANUARY: 'JANUARY',
  FEBRUARY: 'FEBRUARY',
  MARCH: 'MARCH',
  APRIL: 'APRIL',
  MAY: 'MAY',
  JUNE: 'JUNE',
  JULY: 'JULY',
  AUGUST: 'AUGUST',
  SEPTEMBER: 'SEPTEMBER',
  OCTOBER: 'OCTOBER',
  NOVEMBER: 'NOVEMBER',
  DECEMBER: 'DECEMBER',
} as const;

export const MONTH_ABBRS = {
  JANUARY: 'Jan',
  FEBRUARY: 'Feb',
  MARCH: 'Mar',
  APRIL: 'Apr',
  MAY: 'May',
  JUNE: 'Jun',
  JULY: 'Jul',
  AUGUST: 'Aug',
  SEPTEMBER: 'Sep',
  OCTOBER: 'Oct',
  NOVEMBER: 'Nov',
  DECEMBER: 'Dec',
} as const satisfies Record<keyof typeof MONTHS, string>;

export const NOTIFICATION_CATEGORIES = {
  CHALLENGES: 'challenges',
  COUPONS: 'coupons',
  GENERAL: 'general',
  MESSAGES: 'messages',
  PRODUCTS: 'products',
} as const satisfies Record<string, NotificationCategory>;

export const PHONE_FORMATS = {
  GERMANY: {
    LANDLINE: '## ######',
    MOBILE: '#### #######',
  },
  POLAND: {
    LANDLINE: '## ### ## ##',
    MOBILE: '### ### ###',
  },
  UK: {
    LANDLINE: '## #### ####',
    MOBILE: '#### ### ###',
  },
} as const satisfies Record<
  keyof typeof COUNTRIES,
  {
    LANDLINE: string;
    MOBILE: string;
  }
>;

export const STAMP_TYPES = {
  DEFAULT: 'default_stamp',
  // Food and drink
  MUG: 'mug',
  COFFEE_BEAN: 'coffee_bean',
  COFFEE_CUP: 'coffee_cup',
  COFFEE_POT: 'coffee_pot',
  TEA_CUP: 'tea_cup',
  SODA: 'soda',
  BEER_PINT: 'beer_pint',
  COCKTAIL: 'cocktail',
  COCKTAIL_2: 'cocktail_2',
  WINE_GLASS: 'wine_glass',
  BURGER: 'burger',
  PIZZA: 'pizza',
  HOT_DOG: 'hot_dog',
  SANDWICH: 'sandwich',
  SALAD: 'salad',
  CAKE: 'cake',
  CUPCAKE: 'cupcake',
  // Beauty and fashion
  NAIL_POLISH: 'nail_polish',
  HIGH_HEELS: 'high_heels',
  SHOES: 'shoes',
  // Services
  HAIR_DRYER: 'hair_dryer',
  COMB: 'comb',
  SCISSORS: 'scissors',
  ELECTRIC_RAZOR: 'electric_razor',
  SPONGE: 'sponge',
  BICYCLE: 'bicycle',
  CAR: 'car',
  WHEEL: 'wheel',
  HAMMER: 'hammer',
  DRILLER: 'driller',
  SEWING_MACHINE: 'sewing_machine',
  BRUSH: 'brush',
  KEY: 'key',
  PENCIL_AND_RULER: 'pencil_and_ruler',
  // Grocery
  SHOPPING_BAGS: 'shopping_bags',
  APPLE: 'apple',
  TOMATO: 'tomato',
  // Brands
  CAFE_PRZYGODA: 'cafe_przygoda',
  HOCKI_KLOCKI: 'hocki_klocki',
} as const satisfies Record<string, StampType>;

export const STAMP_VALUES = {
  ABOVE: 'above',
  EACH: 'each',
  ITEM: 'item',
  VISIT: 'visit',
  CUSTOM: 'custom',
  UNDEFINED: 'undefined',
} as const satisfies Record<string, StampValue['type']>;

export const VAPID_PUBLIC_KEY =
  'BIpaZwB3vc2m2UPW1L1_9--qRC2F1A6eLIIKv6Cq4eZq587pwhLJDmAcI_2Nr6FZfTWydqJiXw5arUgHvGO6074';

export const WEEKDAYS = {
  MONDAY: 'MONDAY',
  TUESDAY: 'TUESDAY',
  WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY',
  FRIDAY: 'FRIDAY',
  SATURDAY: 'SATURDAY',
  SUNDAY: 'SUNDAY',
} as const;

export const WEEKDAY_NAMES = {
  MONDAY: 'Monday',
  TUESDAY: 'Tuesday',
  WEDNESDAY: 'Wednesday',
  THURSDAY: 'Thursday',
  FRIDAY: 'Friday',
  SATURDAY: 'Saturday',
  SUNDAY: 'Sunday',
} as const satisfies Record<keyof typeof WEEKDAYS, string>;

export const WEEKDAY_ABBRS = {
  MONDAY: 'Mo',
  TUESDAY: 'Tu',
  WEDNESDAY: 'We',
  THURSDAY: 'Th',
  FRIDAY: 'Fr',
  SATURDAY: 'Sa',
  SUNDAY: 'Su',
} as const satisfies Record<keyof typeof WEEKDAYS, string>;
